/**
 * Client-side file parsing for word import.
 *
 * Clean two-column files (txt/csv/tsv/xlsx) are parsed fully client-side.
 * Messy files (PDFs, prose-like text with no clear separator) yield raw text
 * that the caller sends to /api/import-extract for LLM extraction.
 */

export interface ImportedPair {
  hebrew: string;
  translation?: string;
}

export type ParseResult =
  | { kind: "pairs"; pairs: ImportedPair[] }
  | { kind: "rawText"; text: string };

const HEBREW_RE = /[֐-׿]/;

function hasHebrew(s: string): boolean {
  return HEBREW_RE.test(s);
}

/** Fraction of a string's letters that are Hebrew — used to pick the Hebrew column. */
function hebrewRatio(s: string): number {
  const letters = s.replace(/[^A-Za-z֐-׿]/g, "");
  if (!letters) return 0;
  const hebrew = letters.replace(/[^֐-׿]/g, "");
  return hebrew.length / letters.length;
}

/**
 * Split one line into [hebrew, translation] using the first separator that
 * produces a Hebrew side and a non-Hebrew side. Tried in order: tab, comma,
 * semicolon, " - " dash, "=". Returns null if the line has no Hebrew at all.
 */
function parseLine(line: string): ImportedPair | null {
  const trimmed = line.trim();
  if (!trimmed || !hasHebrew(trimmed)) return null;

  for (const sep of ["\t", ",", ";", " - ", " – ", "="]) {
    if (!trimmed.includes(sep)) continue;
    const parts = trimmed.split(sep).map((p) => p.trim()).filter(Boolean);
    if (parts.length < 2) continue;

    // Pick the most-Hebrew part as the term; join the rest as the translation
    const sorted = [...parts].sort((a, b) => hebrewRatio(b) - hebrewRatio(a));
    const hebrew = sorted[0];
    if (!hasHebrew(hebrew)) continue;
    const translation = parts.filter((p) => p !== hebrew).join(", ");

    // If the "translation" is also mostly Hebrew, this isn't a clean pair
    if (hebrewRatio(translation) > 0.5) continue;

    return { hebrew, translation: translation || undefined };
  }

  // No separator: Hebrew-only line
  if (hebrewRatio(trimmed) > 0.5 && trimmed.split(/\s+/).length <= 4) {
    return { hebrew: trimmed };
  }

  return null;
}

/**
 * Parse plain text (txt/csv/tsv content). If at least 60% of the
 * Hebrew-containing lines parse into pairs, we call it clean; otherwise the
 * caller should fall back to LLM extraction with the raw text.
 */
export function parseTextContent(text: string): ParseResult {
  const lines = text.split(/\r?\n/);
  const hebrewLines = lines.filter((l) => hasHebrew(l));
  if (hebrewLines.length === 0) {
    return { kind: "rawText", text };
  }

  const pairs = lines
    .map(parseLine)
    .filter((p): p is ImportedPair => p !== null);

  if (pairs.length >= Math.max(1, Math.floor(hebrewLines.length * 0.6))) {
    return { kind: "pairs", pairs: dedupePairs(pairs) };
  }

  return { kind: "rawText", text };
}

function dedupePairs(pairs: ImportedPair[]): ImportedPair[] {
  const seen = new Set<string>();
  const result: ImportedPair[] = [];
  for (const p of pairs) {
    const key = p.hebrew.replace(/[֑-ׇ]/g, "").trim();
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(p);
  }
  return result;
}

/**
 * Parse a spreadsheet (.xlsx / .xls / .csv opened as workbook).
 * Reads every sheet; each row's most-Hebrew cell is the term, the first
 * non-Hebrew non-empty cell is the translation.
 */
async function parseSpreadsheet(file: File): Promise<ParseResult> {
  const XLSX = await import("xlsx");
  const data = await file.arrayBuffer();
  const workbook = XLSX.read(data);

  const pairs: ImportedPair[] = [];
  for (const sheetName of workbook.SheetNames) {
    const rows = XLSX.utils.sheet_to_json<unknown[]>(workbook.Sheets[sheetName], {
      header: 1,
      defval: "",
    });
    for (const row of rows) {
      const cells = (row as unknown[])
        .map((c) => String(c ?? "").trim())
        .filter(Boolean);
      if (cells.length === 0) continue;

      const hebrewCell = [...cells].sort((a, b) => hebrewRatio(b) - hebrewRatio(a))[0];
      if (!hasHebrew(hebrewCell)) continue;

      const translationCell = cells.find((c) => c !== hebrewCell && hebrewRatio(c) < 0.5);
      pairs.push({ hebrew: hebrewCell, translation: translationCell || undefined });
    }
  }

  if (pairs.length > 0) {
    return { kind: "pairs", pairs: dedupePairs(pairs) };
  }
  // Nothing recognizable — flatten to text for LLM extraction
  const text = workbook.SheetNames.map((n) =>
    XLSX.utils.sheet_to_csv(workbook.Sheets[n])
  ).join("\n");
  return { kind: "rawText", text };
}

/**
 * Extract text from a PDF. Always returns rawText — vocab PDFs vary too much
 * in layout for positional parsing, so the LLM extraction endpoint handles it.
 */
async function parsePdf(file: File): Promise<ParseResult> {
  const pdfjs = await import("pdfjs-dist");
  pdfjs.GlobalWorkerOptions.workerSrc = new URL(
    "pdfjs-dist/build/pdf.worker.min.mjs",
    import.meta.url
  ).toString();

  const data = await file.arrayBuffer();
  const pdf = await pdfjs.getDocument({ data }).promise;

  const pageTexts: string[] = [];
  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i);
    const content = await page.getTextContent();
    const text = content.items
      .map((item) => ("str" in item ? item.str : ""))
      .join(" ");
    pageTexts.push(text);
  }

  const fullText = pageTexts.join("\n");
  // Try clean parsing first (some PDFs are simple tables), else raw
  const asText = parseTextContent(fullText);
  return asText.kind === "pairs" && asText.pairs.length >= 3
    ? asText
    : { kind: "rawText", text: fullText };
}

/**
 * Parse an uploaded file into word pairs (or raw text needing LLM extraction).
 * Supported: .txt, .csv, .tsv, .xlsx, .xls, .pdf
 */
export async function parseImportFile(file: File): Promise<ParseResult> {
  const name = file.name.toLowerCase();

  if (name.endsWith(".xlsx") || name.endsWith(".xls")) {
    return parseSpreadsheet(file);
  }
  if (name.endsWith(".pdf")) {
    return parsePdf(file);
  }
  // txt / csv / tsv / anything text-like
  const text = await file.text();
  return parseTextContent(text);
}
